import { OAuthConnection, OAuthConnections, OAuthConnectionWarning } from './types';
import parseOauthUrl from './parseOauthUrl';

import collectiveApi from 'modules/common/collectiveApi';

// Built per call rather than shared at module scope: `filterProvider` mutates
// these objects, and it also hands one back to the caller. A shared instance
// therefore leaked state between calls and mutated results React Query had
// already cached — changing a cached value in place, so nothing re-rendered.
const emptyConnection = (): OAuthConnection => ({
    connected: false,
    oauth: {
        authorizationUrl: '',
        clientId: '',
        scope: null,
        state: '',
    },
    organization_name: null,
});

interface OAuthConnectionsResponse {
    connections: Record<string, { connected: boolean; uri: string | null; organization_name: string | null }>;
    warnings?: OAuthConnectionWarning[];
}

const filterProvider = (data: OAuthConnectionsResponse, providerName: string): OAuthConnection => {
    const localConnections: Record<keyof OAuthConnections, OAuthConnection> = {
        gusto: emptyConnection(),
        hurdlr: emptyConnection(),
    };

    Object.keys(data.connections).forEach((rawKey) => {
        if (!Object.prototype.hasOwnProperty.call(localConnections, rawKey)) {
            return;
        }
        const key = rawKey as keyof OAuthConnections;
        const local = localConnections[key];
        if (!local) {
            return;
        }
        local.connected = data.connections[rawKey].connected;
        // `parseOauthUrl` reports missing query params as null, but `OAuth` requires
        // strings, so normalise here. An unusable pair is left as the empty default,
        // which reads the same to callers as the previous null did: falsy, so no
        // connect affordance is offered.
        const parsed = parseOauthUrl(data.connections[rawKey].uri ?? '');
        if (parsed.authorizationUrl && parsed.clientId) {
            local.oauth = {
                authorizationUrl: parsed.authorizationUrl,
                clientId: parsed.clientId,
                scope: parsed.scope ?? null,
                state: parsed.state ?? '',
            };
        }
        local.organization_name = data.connections[rawKey].organization_name;
    });

    return localConnections[providerName as keyof OAuthConnections];
};

export const getOAuthConnection = async (providerName: string): Promise<OAuthConnection> => {
    const { data } = await collectiveApi.get<OAuthConnectionsResponse>('oauth/');
    return filterProvider(data, providerName);
};

export const createOAuthConnection = async (
    code: string,
    providerName: string,
    state: string,
    realmId?: string
): Promise<OAuthConnection & { warnings: OAuthConnectionWarning[] }> => {
    const payload = {
        code,
        state,
        realmId,
    };

    const { data } = await collectiveApi.post<OAuthConnectionsResponse>('oauth/', payload);
    return { ...filterProvider(data, providerName), warnings: data.warnings ?? [] };
};

export const deleteOAuthConnection = async (providerName: string): Promise<void> => {
    await collectiveApi.delete('oauth/', { data: { state: providerName } });
};
